import { useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { BsCalendar3 } from "react-icons/bs";
import styled, { keyframes } from "styled-components";
import { media } from "../../helpers/responsive";
import InlineDatepicker from "../inlineDatepicker";
import { format } from "date-fns";
import { padNumberWithZero } from "../../helpers/utils";

interface IDatePickerProps {
  pickType: "start" | "end";
  currDate: Date;
  setDate: (date: Date) => void;
  hour: number;
  setHour: (hour: number) => void;
  minute: number;
  setMinute: (minute: number) => void;
  disableDateTimeDetails?: {
    hour: number;
    minute: number;
    date: Date;
  };
  planMinDate?: Date;
  onPlanLimitInteract?: () => void;
  compact?: boolean;
}

const DatePicker = ({
  pickType,
  currDate,
  setDate,
  hour,
  setHour,
  minute,
  setMinute,
  disableDateTimeDetails,
  planMinDate,
  onPlanLimitInteract,
  compact = false,
}: IDatePickerProps) => {
  const datepickDropdownToggleRef = useRef(null);

  const handleToggleDropdown = () => {
    if (datepickDropdownToggleRef.current) {
      datepickDropdownToggleRef.current.click();
    }
  };
  const dateFormat = compact ? "d MMM yyyy" : "dd MMMM yyyy";

  return (
    <Wrapper $compact={compact}>
      <p className="heading">{pickType == "start" ? "From" : "To"}</p>
      <div className="date-pick-wrapper">
        <input
          type="text"
          readOnly
          onClick={handleToggleDropdown}
          value={`${format(currDate, dateFormat)} - ${padNumberWithZero(
            hour
          )}:${padNumberWithZero(minute)}`}
        />
        <Dropdown>
          <Dropdown.Toggle ref={datepickDropdownToggleRef}>
            <BsCalendar3 style={{ fontSize: "14px", marginBottom: "5px" }} />
          </Dropdown.Toggle>
          <Dropdown.Menu
            className="date-pick-dropdown"
            popperConfig={{ strategy: "fixed" }}
            renderOnMount
          >
            <InlineDatepicker
              pickType={pickType}
              currDate={currDate}
              setDate={setDate}
              hour={hour}
              setHour={setHour}
              minute={minute}
              setMinute={setMinute}
              disableDateTimeDetails={disableDateTimeDetails}
              planMinDate={planMinDate}
              onPlanLimitInteract={onPlanLimitInteract}
            />
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </Wrapper>
  );
};

export default DatePicker;

const fadeInTop = keyframes`
  0% {
    -webkit-transform: translateY(-1px);
            transform: translateY(-1px);
    opacity: 0;
  }
  100% {
    -webkit-transform: translateY(-50);
            transform: translateY(-50);
    opacity: 1;
  }
`;


const Wrapper = styled.div<{ $compact?: boolean }>`
  .heading {
    font-size: ${({ $compact }) => ($compact ? "13px" : "23px")};
    font-weight: ${({ $compact }) => ($compact ? "600" : "400")};
    line-height: ${({ $compact }) => ($compact ? "1.3" : "29.9px")};
    text-align: left;
    margin: ${({ $compact }) => ($compact ? "0 0 6px 0" : "0")};
    color: ${({ theme }) => theme.text};
  }
  .date-pick-wrapper {
    display: flex;
    width: ${({ $compact }) => ($compact ? "100%" : "300px")};
    max-width: 100%;
    border: 1px solid #e9e9e9;
    border-radius: 10px;
    > input[type="text"] {
      width: 100%;
      border: none;
      border-radius: 10px 0px 0px 10px;
      padding: 10px;
      border: ${({ theme }) => theme.date_range_picker.custom.border};
      color: ${({ theme }) => theme.date_range_picker.custom.color};
      background: ${({ theme }) => theme.date_range_picker.custom.background};
    }
    .dropdown-toggle::after {
      content: none;
    }
    .dropdown {
      .dropdown-toggle {
        border: none;
        border-radius: 0px 10px 10px 0px;
        height: 100%;
        color: ${({ theme }) => theme.text};
        background-color: ${({ theme }) => theme.date_range_picker.custom.background};
      }
      .date-pick-dropdown {
        animation: ${fadeInTop} 0.2s;
        min-width: 250px;
        padding: 0px;
        border: 1px solid ${({ theme }) => theme.border_color};
        border-radius: 10px;
        box-shadow: 5px 5px 30px rgba(0, 0, 0, 0.2);
        .inline-date-picker {
          margin: 0px;
        }
      }
    }
  }

  ${media.down.xs} {
    .heading {
      font-size: 16px;
      line-height: 1.4;
    }
    .date-pick-wrapper {
      width: 100%;
      > input[type="text"] {
        font-size: 13px;
        padding: 8px;
      }
    }
  }
`;
